const { Type } = require('@sinclair/typebox');
const { merge } = require('lodash');
const Blockly = require('blockly');
const setupAddCodeProperties = require('../../../../helpers/addCodeProperties');
const convertToBoolean = require('../../../../helpers/convertToBoolean');
const { moduleRegistration } = require('@sstdev/lib_metadata-config');

const TOOLTIP = 'Container to allow the user to select one or more groups of form elements.';

// NOTE: ElementGroups is intentionally pluralized to indicate that it is a container for multiple groups (of form elements.)
/**
 *
 * @param {string} color
 * @param {*} blocklyBlock
 * @param {*} mdcBlock
 * @param {*} line1OfInputs
 */
function addBlockSpecificFields(color, blocklyBlock, mdcBlock, line1OfInputs) {
    blocklyBlock.setTooltip(TOOLTIP);

    //set inputs to be arranged horizontally (this is the default)
    //Meaning each (dummy)input continues on the top line followed by  the access modifiers
    blocklyBlock.setInputsInline(false);
    // line1 already includes the title and the action modifier gap
    line1OfInputs.appendField('  Property Name:').appendField(new Blockly.FieldTextInput('events'), 'propertyName');
    line1OfInputs
        .appendField('  Retain Parent Context:')
        .appendField(new Blockly.FieldCheckbox('false'), 'retainParentContext');

    blocklyBlock.appendStatementInput('optionGroups').setCheck(['layout']).appendField('Available Groups:');
}

/**
 *
 * @param {*} code
 * @param {*} blocklyBlock
 * @returns {Object}
 */
function convertCustomFieldsToCode(code, blocklyBlock) {
    const addCodeProperties = setupAddCodeProperties(code, blocklyBlock);
    // title, propertyName and action modifiers are handled in the base class
    // extracting children is handled in the base class
    //all we need to do here is add the sequence
    addCodeProperties([
        'propertyName',
        { key: 'retainParentContext', handler: convertToBoolean },
        {
            key: 'sequence',
            // Song and dance to figure out the sequence based on order in the workspace.
            handler: () => {
                const parent = blocklyBlock.getSurroundParent();
                // if the block is not docked, it won't have a parent.
                // Therefore, our sequence # is by definition 0:
                if (!parent) return 0;
                const parentChildren = parent.getDescendants(false);
                return parentChildren.findIndex(value => value.id === blocklyBlock.id) - 1; // subtract 1 because includes parent with getDescendants
            }
        }
    ]);
    return code;
}

function customizeToolbox(block) {
    block.inputs = {
        optionGroups: {
            block: {
                type: 'layout_LeftAlignLayout',
                fields: { title: 'Option 1' },
                inputs: {
                    children: {
                        block: {
                            type: 'form_SubForm',
                            inputs: {
                                children: {
                                    block: {
                                        type: 'formElement_InvisibleElement',
                                        fields: {
                                            title: 'Type',
                                            propertyName: 'type',
                                            required: true,
                                            defaultValue: 'option1'
                                        }
                                    }
                                }
                            }
                        }
                    }
                },
                next: {
                    block: {
                        type: 'layout_LeftAlignLayout',
                        fields: { title: 'Option 2' },
                        inputs: {
                            children: {
                                block: {
                                    type: 'form_SubForm',
                                    inputs: {
                                        children: {
                                            block: {
                                                type: 'formElement_InvisibleElement',
                                                fields: {
                                                    title: 'Type',
                                                    propertyName: 'type',
                                                    required: true,
                                                    defaultValue: 'option2'
                                                }
                                            }
                                        }
                                    }
                                }
                            }
                        }
                    }
                }
            }
        }
    };
}

/** Builds the TypeBox schema fragment for the ElementGroups form element. */
function asDataDictionary(hNode, context) {
    const { id, title, propertyName, required } = hNode;
    // reuse consistent flags
    const { isRequired, isEditable, isOptional } = moduleRegistration.resolveFieldFlags(required, context);

    // Gather option schemas from children and merge their subDataDictionaries
    const optionBuilds =
        (hNode.children || []).map((child, index) => {
            const [childSubDD = {}, childPartialMap = {}] = context.extractFromChild(child, context) || [{}, {}];

            // Each option has an optional "id" (default to child.title) + whatever the child contributed
            // id is optional to allow payloads that use 'type' as the discriminator for anyOf matching
            const optionSchema = Type.Object(
                {
                    id: Type.Optional(
                        Type.String({
                            title: 'id',
                            'x-propertyName': 'id',
                            'x-defaultValue': child.title,
                            'x-optional': true,
                            'x-editable': false
                        })
                    ),
                    // childPartialMap is a plain object: prop -> TypeBox schema
                    ...childPartialMap
                },
                {
                    title: String(index),
                    // additionalProperties: true allows extending options with extra properties
                    // This enables dynamic property addition beyond the defined schema
                    additionalProperties: true
                }
            );

            return { childSubDD, optionSchema };
        }) || [];

    // Merge all child sub dictionaries using reduce to accumulate all nested schemas
    const subDataDictionary = optionBuilds.reduce((acc, cur) => merge(acc, cur.childSubDD), {}) || {};

    // Build the value schema: Array of union of option objects (or generic object if no children)
    // Type.Union creates JSON Schema union type - validates against any of the provided schemas
    const optionSchemas = optionBuilds.map(o => o.optionSchema);
    const arrayItems =
        optionSchemas.length > 0 ? Type.Union(optionSchemas) : Type.Object({}, { additionalProperties: true });

    const baseArraySchema = Type.Array(arrayItems, {
        title,
        'x-id': id,
        'x-dataType': 'array',
        'x-propertyName': propertyName,
        'x-required': isRequired,
        'x-optional': isOptional,
        'x-editable': isEditable
    });

    // Type.Optional makes the entire array optional if the field is not required
    const valueSchema = isOptional ? Type.Optional(baseArraySchema) : baseArraySchema;

    // Return plain map for partialTypeDefinition - this defines the property schema
    // The key is the dynamic propertyName, value is the TypeBox schema
    const partialTypeDefinition = {
        [propertyName]: valueSchema
    };

    return [subDataDictionary, partialTypeDefinition];
}

module.exports = { addBlockSpecificFields, convertCustomFieldsToCode, customizeToolbox, asDataDictionary };
